/**
 * MUI Theme Configuration
 * 
 * Extended MUI theme based on design system tokens.
 */

import { createTheme, alpha } from '@mui/material/styles';
import { colors, typography, radii, shadows, transitions, components } from './tokens';

// Extend MUI's palette to include custom colors
declare module '@mui/material/styles' {
  interface Palette {
    neutral: typeof colors.neutral;
  }
  interface PaletteOptions {
    neutral?: typeof colors.neutral;
  }
}

export const insightsTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary[500],
      light: colors.primary[300],
      dark: colors.primary[700],
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: colors.neutral[600],
      light: colors.neutral[400],
      dark: colors.neutral[800],
      contrastText: '#FFFFFF',
    },
    success: {
      main: colors.success.main,
      light: colors.success.light,
      dark: colors.success.dark,
    },
    warning: {
      main: colors.warning.main,
      light: colors.warning.light,
      dark: colors.warning.dark,
    },
    error: {
      main: colors.error.main,
      light: colors.error.light,
      dark: colors.error.dark,
    },
    info: {
      main: colors.info.main,
      light: colors.info.light,
      dark: colors.info.dark,
    },
    background: {
      default: colors.neutral[100],
      paper: colors.neutral[0],
    },
    text: {
      primary: colors.neutral[900],
      secondary: colors.neutral[600],
    },
    divider: colors.neutral[200],
    // @ts-expect-error - Custom neutral palette extension
    neutral: colors.neutral,
  },
  
  typography: {
    fontFamily: typography.fontFamily.body,
    
    h1: {
      fontFamily: typography.fontFamily.display,
      fontSize: typography.fontSize['3xl'],
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
      color: colors.neutral[900],
    },
    h2: {
      fontFamily: typography.fontFamily.display,
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
      color: colors.neutral[900],
    },
    h3: {
      fontFamily: typography.fontFamily.display,
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.tight,
      color: colors.neutral[900],
    },
    h4: {
      fontFamily: typography.fontFamily.display,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.normal,
      color: colors.neutral[900],
    },
    h5: {
      fontFamily: typography.fontFamily.display,
      fontSize: typography.fontSize.md,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
      color: colors.neutral[900],
    },
    h6: {
      fontFamily: typography.fontFamily.display,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
      color: colors.neutral[900],
    },
    subtitle1: {
      fontSize: typography.fontSize.md,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
      color: colors.neutral[700],
    },
    subtitle2: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
      color: colors.neutral[600],
    },
    body1: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.normal,
      lineHeight: typography.lineHeight.relaxed,
      color: colors.neutral[700],
    },
    body2: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.normal,
      lineHeight: typography.lineHeight.relaxed,
      color: colors.neutral[600],
    },
    caption: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.normal,
      lineHeight: typography.lineHeight.normal,
      color: colors.neutral[500],
    },
    overline: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.normal,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: colors.neutral[500],
    },
    button: {
      fontFamily: typography.fontFamily.body,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      textTransform: 'none',
    },
  },
  
  shape: {
    borderRadius: radii.md,
  },
  
  shadows: [
    shadows.none,
    shadows.xs,
    shadows.sm,
    shadows.sm,
    shadows.md,
    shadows.md,
    shadows.md,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
  ],
  
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.neutral[100],
        },
      },
    },
    
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: components.button.borderRadius,
          fontWeight: typography.fontWeight.medium,
          transition: transitions.fast,
          textTransform: 'none',
        },
        sizeMedium: {
          height: components.button.height.md,
          padding: '8px 20px',
        },
        sizeSmall: {
          height: components.button.height.sm,
          padding: '6px 16px',
          fontSize: typography.fontSize.sm,
        },
        sizeLarge: {
          height: components.button.height.lg,
          padding: '12px 28px',
          fontSize: typography.fontSize.md,
        },
        containedPrimary: {
          backgroundColor: colors.primary[500],
          '&:hover': {
            backgroundColor: colors.primary[600],
          },
        },
        outlinedPrimary: {
          borderColor: colors.primary[500],
          color: colors.primary[500],
          '&:hover': {
            backgroundColor: alpha(colors.primary[500], 0.08),
            borderColor: colors.primary[600],
          },
        },
        textPrimary: {
          color: colors.primary[500],
          '&:hover': {
            backgroundColor: alpha(colors.primary[500], 0.08),
          },
        },
      },
    },
    
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          transition: transitions.fast,
          '&:hover': {
            backgroundColor: colors.neutral[100],
          },
        },
      },
    },
    
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${colors.neutral[200]}`,
          borderRadius: components.card.borderRadius,
        },
        elevation0: {
          boxShadow: shadows.none,
        },
        elevation1: {
          boxShadow: shadows.card,
        },
        elevation2: {
          boxShadow: shadows.elevated,
        },
      },
    },
    
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: components.card.borderRadius,
          border: `1px solid ${colors.neutral[200]}`,
        },
      },
    },
    
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: components.card.padding,
          '&:last-child': {
            paddingBottom: components.card.padding,
          },
        },
      },
    },
    
    MuiChip: {
      styleOverrides: {
        root: {
          height: components.chip.height,
          borderRadius: components.chip.borderRadius,
          fontWeight: typography.fontWeight.medium,
          fontSize: typography.fontSize.sm,
        },
        filled: {
          backgroundColor: colors.neutral[100],
          color: colors.neutral[700],
          '&:hover': {
            backgroundColor: colors.neutral[200],
          },
        },
        outlined: {
          borderColor: colors.neutral[300],
        },
        colorPrimary: {
          backgroundColor: alpha(colors.primary[500], 0.12),
          color: colors.primary[700],
          border: `1px solid ${alpha(colors.primary[500], 0.24)}`,
          '&:hover': {
            backgroundColor: alpha(colors.primary[500], 0.2),
          },
        },
      },
    },
    
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
    },
    
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: components.input.borderRadius,
          backgroundColor: components.input.background,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.neutral[400],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: components.input.focusBorderColor,
            borderWidth: 2,
          },
        },
        notchedOutline: {
          borderColor: components.input.borderColor,
          transition: transitions.fast,
        },
        input: {
          padding: '10px 14px',
        },
      },
    },
    
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: 'auto',
        },
      },
    },
    
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: typography.fontSize.base,
          fontWeight: typography.fontWeight.medium,
          color: colors.neutral[700],
          '&.Mui-focused': {
            color: colors.primary[600],
          },
        },
      },
    },
    
    MuiTabs: {
      styleOverrides: {
        indicator: {
          display: 'none',
        },
      },
    },
    
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: typography.fontWeight.medium,
          fontSize: typography.fontSize.base,
          minHeight: components.navItem.height,
          padding: '8px 16px',
          borderRadius: radii.md,
          transition: transitions.fast,
          '&:hover': {
            backgroundColor: components.navItem.hoverBackground,
          },
          '&.Mui-selected': {
            backgroundColor: components.navItem.activeBackground,
            color: colors.primary[700],
          },
        },
      },
    },
    
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.neutral[200],
        },
      },
    },
    
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.neutral[800],
          fontSize: typography.fontSize.xs,
          fontWeight: typography.fontWeight.medium,
          padding: '6px 12px',
          borderRadius: radii.sm,
        },
        arrow: {
          color: colors.neutral[800],
        },
      },
    },
    
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: 0,
          minHeight: 48,
          '&.Mui-expanded': {
            minHeight: 48,
          },
        },
        content: {
          margin: '12px 0',
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primary[100],
          color: colors.primary[700],
          fontWeight: typography.fontWeight.semibold,
        },
      },
    },
    
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: typography.fontWeight.semibold,
          fontSize: typography.fontSize.xs,
        },
      },
    },
  },
});

export default insightsTheme;

